/**
 * Hook for managing chat stream control operations.
 *
 * Handles:
 * - Aborting active streams
 * - Retrying the last message after errors
 * - Analytics tracking for stream interruptions
 */

import { chatAbortControllersAtom } from '@/atoms/chatAtoms';
import { chatCooldownUntilAtomFamily } from '@/atoms/chatCooldownAtoms';
import { isCooldownActive } from '@/components/chat/utils/chatCooldown';
import type { ChatMode, ChatState, SessionConversation, TimelineEntry } from '@/components/chat/types/types';
import { cancelTracking, startTracking, TrackingKey } from '@heidi/runtime/performance';
import { trackChatStreamInterrupted } from '@/lib/analytics/scribe/chat';
import { logger } from '@/utils/logger/logger';
import { abortStreamWithReason, STREAM_ABORT_REASON } from '@/utils/streamAbortReason';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { getLastUserQuery } from '../utils/chatHelpers';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';

interface UseChatStreamControlParams {
  /** State key for the current chat */
  stateKey: string;
  /** Analytics session ID */
  analyticsSessionId: string;
  /** Current chat ID */
  chatId: string | null | undefined;
  /** Session ID (null if sessionless) */
  sessionId: string | null | undefined;
  /** Current conversation object */
  conversation: SessionConversation;
  /** Function to update chat state */
  setChatState: (fn: (prev: ChatState) => ChatState) => void;
  /** Function to start streaming */
  startStreaming: (
    timeline: TimelineEntry[],
    overrideMode?: ChatMode,
    onError?: (err: string) => void,
    chatId?: string | null,
    files?: File[],
    attachmentIds?: string[]
  ) => Promise<void>;
}

interface UseChatStreamControlReturn {
  /** Abort the current stream */
  abortStream: (isUserInitiated?: boolean) => void;
  /** Retry the last message after an error */
  retryLastMessage: () => void;
}

/**
 * Hook that provides stream control functions for chat functionality.
 */
export function useChatStreamControl({
  stateKey,
  analyticsSessionId,
  chatId,
  sessionId,
  conversation,
  setChatState,
  startStreaming,
}: UseChatStreamControlParams): UseChatStreamControlReturn {
  const { trackChatAborted } = useEvidenceAnalytics();
  const setAbortController = useSetAtom(chatAbortControllersAtom(stateKey));
  const store = getDefaultStore();

  const abortStream = useCallback(
    (isUserInitiated = true) => {
      const currentAbortController = store.get(chatAbortControllersAtom(stateKey));
      if (currentAbortController) {
        abortStreamWithReason(
          currentAbortController,
          isUserInitiated ? STREAM_ABORT_REASON.USER_CANCEL : STREAM_ABORT_REASON.PROGRAMMATIC
        );
        setAbortController(null);
        cancelTracking({ key: TrackingKey.EVIDENCE_MESSAGE_SEND });
        cancelTracking({ key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK });

        setChatState((prev) => {
          const session = prev[stateKey];
          if (!session) return prev;

          const timelineWithoutPlaceholder = session.timeline.filter(
            (entry: TimelineEntry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
          );

          return {
            ...prev,
            [stateKey]: { ...session, timeline: timelineWithoutPlaceholder, streamingState: null },
          };
        });

        if (isUserInitiated) {
          const lastQuery = getLastUserQuery(conversation.timeline);
          trackChatStreamInterrupted({
            sessionId: analyticsSessionId,
            messageId: conversation.streamingState?.messageId,
          });

          const streamStartedAt = conversation.streamingState?.streamStartedAt;
          trackChatAborted({
            durationMs: streamStartedAt ? Date.now() - streamStartedAt : undefined,
            queryLength: lastQuery?.length || 0,
            chatId: chatId ?? undefined,
            messageId: conversation.streamingState?.messageId,
            sessionId: sessionId ?? undefined,
          });
        }
      }
    },
    [
      stateKey,
      analyticsSessionId,
      setChatState,
      conversation.timeline,
      store,
      setAbortController,
      trackChatAborted,
      chatId,
      sessionId,
      conversation.streamingState?.streamStartedAt,
    ]
  );

  const retryLastMessage = useCallback(() => {
    if (isCooldownActive(store.get(chatCooldownUntilAtomFamily(stateKey)))) {
      return;
    }

    const currentAbortController = store.get(chatAbortControllersAtom(stateKey));
    if (currentAbortController) {
      abortStreamWithReason(currentAbortController, STREAM_ABORT_REASON.RETRY);
      setAbortController(null);
    }

    const timelineWithoutErrors = conversation.timeline.filter(
      (entry) => entry.type !== 'error_item'
    );

    if (timelineWithoutErrors.length === 0) return;

    const lastQuery = getLastUserQuery(timelineWithoutErrors);
    startTracking({
      key: TrackingKey.EVIDENCE_MESSAGE_SEND,
      metadata: {
        chat_id: chatId ?? null,
        session_id: sessionId ?? null,
        entry_method: 'retry',
        message_length: lastQuery?.length ?? 0,
      },
    });

    setTimeout(() => {
      void startStreaming(timelineWithoutErrors).catch((error: unknown) => {
        logger.error('Failed to retry chat stream', {
          error,
          chatId: chatId ?? null,
          sessionId: sessionId ?? null,
        });
      });
    }, 100);
  }, [conversation.timeline, startStreaming, store, stateKey, setAbortController, chatId, sessionId]);

  return { abortStream, retryLastMessage };
}
